import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, Plus, ArrowUp, ArrowDown, Upload, Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/imageUrl';
import { API_URL } from '@/config/api';
import { useImageUpload } from '@/hooks/useImageUpload';

const DISPLAY_OPTIONS = [
  { value: 'marquee', label: '走馬燈（自動滾動）' },
  { value: 'grid', label: '相片格（靜態）' },
];

// Multi-select image picker dialog
const MultiImagePicker = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/images`);
      setImages(res.data);
    } finally {
      setLoading(false);
    }
  };

  const { uploadFiles, uploading, fileInputRef } = useImageUpload(fetchImages);

  useEffect(() => {
    if (open) fetchImages();
    else setSelected(new Set());
  }, [open]);

  const toggle = (url) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const handleConfirm = () => {
    if (!selected.size) return toast.error('請先選擇圖片');
    onAdd([...selected]);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button"><Plus className="mr-2 h-4 w-4" />選擇相片加入走馬燈</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>選擇相片（可多選）</DialogTitle>
        </DialogHeader>

        {/* Drop zone */}
        <div
          onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors flex-shrink-0 ${
            dragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary hover:bg-gray-50'
          }`}
        >
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={(e) => uploadFiles(e.target.files)} className="hidden" />
          <Upload className={`mx-auto h-6 w-6 mb-1 ${dragOver ? 'text-primary' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-500">
            {uploading ? '上傳中...' : dragOver ? '放開以上傳' : '拖放圖片上傳，或點擊選擇'}
          </p>
        </div>

        {/* Image grid */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">載入中...</div>
          ) : images.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">未有圖片，請先上傳</div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 p-1">
              {images.map((img) => {
                const isSelected = selected.has(img.url);
                return (
                  <div
                    key={img.id}
                    onClick={() => toggle(img.url)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={resolveImageUrl(img.url)} alt={img.original_name} className="w-full h-full object-cover" />
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 bg-primary rounded-full p-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-xs p-1 truncate">
                      {img.original_name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-3 border-t flex-shrink-0">
          <span className="text-sm text-muted-foreground">
            已選 {selected.size} 張
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>取消</Button>
            <Button onClick={handleConfirm} disabled={!selected.size}>
              加入走馬燈 ({selected.size})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [settings, setSettings] = useState({ gallery_speed: 30, gallery_display: 'marquee', gallery_height: 192 });
  const [editingCaption, setEditingCaption] = useState(null); // { id, value }

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [galleryRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/gallery`),
        axios.get(`${API_URL}/settings`),
      ]);
      setItems(galleryRes.data);
      setSettings({
        gallery_speed: settingsRes.data.gallery_speed ?? 30,
        gallery_display: settingsRes.data.gallery_display ?? 'marquee',
        gallery_height: settingsRes.data.gallery_height ?? 192,
      });
    } catch { toast.error('載入失敗'); }
    finally { setLoading(false); }
  };

  const handleAddMultiple = async (urls) => {
    try {
      for (const url of urls) {
        await axios.post(`${API_URL}/gallery`, { image_url: url, caption: '' });
      }
      toast.success(`已加入 ${urls.length} 張相片`);
      fetchAll();
    } catch { toast.error('新增失敗'); }
  };

  const deleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/gallery/${deleteTarget}`);
      toast.success('已刪除');
      fetchAll();
    } catch { toast.error('刪除失敗'); }
    finally { setDeleteTarget(null); }
  };

  const moveItem = async (index, dir) => {
    const newItems = [...items];
    const target = index + dir;
    if (target < 0 || target >= newItems.length) return;
    [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
    setItems(newItems);
    await axios.put(`${API_URL}/gallery/reorder`, newItems.map(i => i.id));
  };

  const saveCaption = async (id, caption) => {
    try {
      await axios.patch(`${API_URL}/gallery/${id}`, { caption });
      setItems(prev => prev.map(i => i.id === id ? { ...i, caption } : i));
      toast.success('說明已更新');
    } catch { toast.error('更新失敗'); }
    finally { setEditingCaption(null); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('設定已儲存');
    } catch { toast.error('儲存失敗'); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="text-center py-12">載入中...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">相片走馬燈</h1>
          <p className="text-muted-foreground">管理首頁學員分享下方的相片展示</p>
        </div>

        {/* Settings */}
        <Card>
          <CardHeader><CardTitle>顯示設定</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>顯示方式</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-2"
                  value={settings.gallery_display}
                  onChange={(e) => setSettings({ ...settings, gallery_display: e.target.value })}
                >
                  {DISPLAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {settings.gallery_display === 'marquee' && (
                <div>
                  <Label>滾動速度：{settings.gallery_speed <= 15 ? '快' : settings.gallery_speed <= 35 ? '中' : '慢'} ({settings.gallery_speed}秒/圈)</Label>
                  <input
                    type="range" min={8} max={60} step={2}
                    value={settings.gallery_speed}
                    onChange={(e) => setSettings({ ...settings, gallery_speed: Number(e.target.value) })}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>快</span><span>慢</span>
                  </div>
                </div>
              )}
            <div>
                <Label>相片高度：{settings.gallery_height}px</Label>
                <input
                  type="range" min={80} max={400} step={8}
                  value={settings.gallery_height}
                  onChange={(e) => setSettings({ ...settings, gallery_height: Number(e.target.value) })}
                  className="w-full mt-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>小 (80px)</span><span>大 (400px)</span>
                </div>
                {/* Live preview */}
                <div className="mt-2 bg-gray-100 rounded border overflow-hidden flex items-center justify-center text-xs text-muted-foreground" style={{ height: `${settings.gallery_height}px` }}>
                  預覽高度
                </div>
              </div>
            </div>
            <Button onClick={saveSettings} disabled={saving}>{saving ? '儲存中...' : '儲存設定'}</Button>
          </CardContent>
        </Card>

        {/* Add photos */}
        <Card>
          <CardHeader><CardTitle>新增相片</CardTitle></CardHeader>
          <CardContent>
            <MultiImagePicker onAdd={handleAddMultiple} />
            <p className="text-xs text-muted-foreground mt-2">從圖片管理庫中選擇，可一次選多張。如需上傳新相片，可在選擇器內直接拖放上傳。</p>
          </CardContent>
        </Card>

        {/* Gallery list */}
        <Card>
          <CardHeader><CardTitle>相片列表（{items.length} 張）</CardTitle></CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">尚未加入任何相片，點擊上方「選擇相片」開始</p>
            ) : (
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                    <img
                      src={resolveImageUrl(item.image_url)}
                      alt={item.caption || `相片 ${idx + 1}`}
                      className="w-20 h-16 object-cover rounded border flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      {/* Editable caption */}
                      {editingCaption?.id === item.id ? (
                        <div className="flex gap-2 items-center">
                          <Input
                            autoFocus
                            value={editingCaption.value}
                            onChange={(e) => setEditingCaption({ ...editingCaption, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveCaption(item.id, editingCaption.value);
                              if (e.key === 'Escape') setEditingCaption(null);
                            }}
                            placeholder="輸入說明文字..."
                            className="h-8 text-sm"
                          />
                          <Button size="sm" onClick={() => saveCaption(item.id, editingCaption.value)}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingCaption(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-1.5 cursor-pointer group/cap"
                          onClick={() => setEditingCaption({ id: item.id, value: item.caption || '' })}
                          title="點擊編輯說明"
                        >
                          <p className="text-sm text-gray-700 truncate">
                            {item.caption || <span className="text-muted-foreground italic">（無說明，點擊新增）</span>}
                          </p>
                          <Pencil className="h-3 w-3 text-gray-400 group-hover/cap:text-primary flex-shrink-0" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}>
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(item.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此相片？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，相片將從走馬燈中永久移除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={deleteItem} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminGallery;
